import { z } from 'zod';

export const FILTER_OPERATORS = [
  'equals',
  'notEquals',
  'contains',
  'notContains',
  'startsWith',
  'notStartsWith',
  'endsWith',
  'notEndsWith',
  'gt',
  'gte',
  'lt',
  'lte',
  'set',
  'notSet',
  'inDateRange',
  'notInDateRange',
  'beforeDate',
  'beforeOrOnDate',
  'afterDate',
  'afterOrOnDate'
] as const;

export type FilterOperator = (typeof FILTER_OPERATORS)[number];

const cubeFilter = z.object({
  member: z.string().describe('Cube member path'),
  operator: z
    .union(
      FILTER_OPERATORS.map(op => z.literal(op)) as [
        z.ZodLiteral<FilterOperator>,
        z.ZodLiteral<FilterOperator>,
        ...z.ZodLiteral<FilterOperator>[]
      ]
    )
    .describe('Filter operator to apply'),
  values: z
    .array(z.string())
    .optional()
    .describe('Values to filter by; omit for set/notSet operators')
});

const timeDimension = z.object({
  dimension: z.string().describe('Time dimension path, e.g. "Orders.createdAt"'),
  dateRange: z.union([
    z.array(z.string()).optional().describe('Date range as [start, end] ISO strings'),
    z.string().optional().describe('Relative date range, e.g. "last 7 days", "this month"')
  ]),
  granularity: z
    .string()
    .optional()
    .describe('Time granularity: "day", "week", "month", "quarter", "year"')
});

export const CubeQuerySchema = z.object({
  measures: z
    .array(z.string())
    .optional()
    .describe(
      'Aggregated values (sum, count, avg, max, min, number) to compute. ONLY include members listed under "Measures:" in the cube metadata. Examples: actual_spend, planned_spend, uncapped_revenue, count.'
    ),
  dimensions: z
    .array(z.string())
    .optional()
    .describe(
      'Non-aggregated attributes to group by. ONLY include members listed under "Dimensions:" in the cube metadata. Examples: campaign_name, stage, flight_date. NEVER put measures here.'
    ),
  timeDimensions: z
    .array(timeDimension)
    .optional()
    .describe('Time-based dimensions with optional date range and granularity'),
  filters: z.array(cubeFilter).optional().describe('Filters to narrow the result set'),
  // Array form required for LLM structured outputs (additionalProperties must be false; z.record() not supported).
  // Converted to object form for Cube SDK in toTypedCubeQuery().
  order: z
    .array(z.tuple([z.string(), z.union([z.literal('asc'), z.literal('desc')])]))
    .optional()
    .describe(
      'Sort order as array of [memberPath, direction] tuples, e.g. [["line_items.count", "asc"]]'
    ),
  limit: z.number().optional().describe('Max number of rows to return'),
  offset: z.number().optional().describe('Number of rows to skip for pagination')
});

export type CubeQuerySchema = z.infer<typeof CubeQuerySchema>;
